import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Turnstile } from './Turnstile';
import { useAuth } from '../context';
import type { RegisterRequest } from '../types';
import logo from '../../logo.png';

// Схема валидации для формы регистрации
const registerSchema = z.object({
  username: z
    .string()
    .min(4, 'Имя пользователя должно содержать минимум 4 символа')
    .regex(/^[a-zA-Z]+$/, 'Имя пользователя должно содержать только английские буквы'),
  password: z
    .string()
    .min(10, 'Пароль должен содержать минимум 10 символов')
    .regex(/^(?=.*[a-zA-Z])(?=.*\d)/, 'Пароль должен содержать буквы и цифры'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Пароли не совпадают',
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

export function RegisterForm({ onSuccess, onSwitchToLogin }: RegisterFormProps) {
  const { register, isLoading } = useAuth();
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    if (!turnstileToken) {
      setError('Пожалуйста, пройдите проверку безопасности');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');

      const registerData: RegisterRequest = {
        username: data.username,
        password: data.password,
        turnstile_token: turnstileToken,
      };

      await register(registerData);
      setSuccess('Регистрация прошла успешно! Теперь вы можете войти в систему.');
      
      // Очищаем форму после успешной регистрации
      form.reset();
      setTurnstileToken('');
      
      // Вызываем callback успеха через небольшую задержку
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка регистрации';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token);
    setError(''); // Очищаем ошибку при успешной верификации
  };

  const handleTurnstileError = (error: string) => {
    setTurnstileToken('');
    setError('Ошибка проверки безопасности. Попробуйте еще раз.');
  };

  const isFormDisabled = isLoading || isSubmitting;

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-4">
          <img src={logo} alt="RUTON" className="w-24 h-24 invert" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">Регистрация</CardTitle>
        <CardDescription className="text-center">
          Создайте новый аккаунт для доступа к системе
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Имя пользователя</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Введите имя пользователя"
                      disabled={isFormDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <div className="text-xs text-muted-foreground">
                    Только английские буквы, минимум 4 символа
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Пароль</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Введите пароль"
                      disabled={isFormDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <div className="text-xs text-muted-foreground">
                    Минимум 10 символов, должен содержать буквы и цифры
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Подтвердите пароль</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Повторите пароль"
                      disabled={isFormDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label className="text-sm font-medium">Проверка безопасности</Label>
              <Turnstile
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                className="flex justify-center"
              />
            </div>

            {error && (
              <div className="text-sm text-destructive font-medium text-center">
                {error}
              </div>
            )}

            {success && (
              <div className="text-sm text-green-600 font-medium text-center">
                {success}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isFormDisabled || !turnstileToken}
            >
              {isSubmitting ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>

            {onSwitchToLogin && (
              <div className="text-center text-sm">
                <span className="text-muted-foreground">Уже есть аккаунт? </span>
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto font-normal"
                  onClick={onSwitchToLogin}
                  disabled={isFormDisabled}
                >
                  Войти
                </Button>
              </div>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
